import axios, { AxiosError } from 'axios';
import type { AxiosInstance, InternalAxiosRequestConfig, AxiosResponse } from 'axios';
import type { ApiError } from '../types/api.types';
import { notifyError } from '../utils/notifications';

// Retry configuration with exponential backoff
const MAX_RETRIES = 3;
const INITIAL_RETRY_DELAY = 1000; // 1 second
const MAX_RETRY_DELAY = 10000; // 10 seconds

// Request deduplication - prevent duplicate requests
const pendingRequests = new Map<string, Promise<any>>();

// Track failed requests to implement backoff
const failedRequests = new Map<string, { count: number; lastAttempt: number }>();

// Helper to generate request key for deduplication
const getRequestKey = (config: InternalAxiosRequestConfig): string => {
  return `${config.method}:${config.url}:${JSON.stringify(config.params)}`;
};

// Helper to check if we should retry based on backoff
const shouldRetry = (requestKey: string): boolean => {
  const failureInfo = failedRequests.get(requestKey);
  if (!failureInfo) return true;
  
  if (failureInfo.count >= MAX_RETRIES) {
    return false;
  }
  
  // Calculate backoff delay
  const backoffDelay = Math.min(
    INITIAL_RETRY_DELAY * Math.pow(2, failureInfo.count),
    MAX_RETRY_DELAY
  );
  
  const timeSinceLastAttempt = Date.now() - failureInfo.lastAttempt;
  return timeSinceLastAttempt >= backoffDelay;
};

// Helper to record failed request
const recordFailure = (requestKey: string) => {
  const existing = failedRequests.get(requestKey);
  failedRequests.set(requestKey, {
    count: existing ? existing.count + 1 : 1,
    lastAttempt: Date.now(),
  });
  
  // Clean up old entries after 5 minutes
  setTimeout(() => {
    failedRequests.delete(requestKey);
  }, 5 * 60 * 1000);
};

// Helper to clear failure record on success
const clearFailure = (requestKey: string) => {
  failedRequests.delete(requestKey);
};

// Create Axios instance with base configuration
const apiClient: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Store for auth token getter (will be set by auth store)
let getAuthToken: (() => string | null) | null = null;
let clearAuthToken: (() => void) | null = null;

// Function to set auth token getter from auth store
export const setAuthTokenGetter = (getter: () => string | null, clearer: () => void) => {
  getAuthToken = getter;
  clearAuthToken = clearer;
};

// Request interceptor - Attach JWT token and handle deduplication
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Get token from auth store if available
    if (getAuthToken) {
      const token = getAuthToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    
    // Check if we should apply backoff for this request
    const requestKey = getRequestKey(config);
    if (!shouldRetry(requestKey)) {
      const failureInfo = failedRequests.get(requestKey);
      const backoffDelay = Math.min(
        INITIAL_RETRY_DELAY * Math.pow(2, failureInfo!.count - 1),
        MAX_RETRY_DELAY
      );
      const error = new Error(
        `Too many failed attempts. Please wait ${Math.ceil(backoffDelay / 1000)} seconds before retrying.`
      ) as any;
      error.isBackoff = true;
      return Promise.reject(error);
    }
    
    // Request deduplication for GET requests only
    if (config.method?.toLowerCase() === 'get') {
      const existingRequest = pendingRequests.get(requestKey);
      if (existingRequest) {
        // Return the existing promise instead of making a new request
        return existingRequest.then(() => config);
      }
    }
    
    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

// Response interceptor - Handle errors globally
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    // Clear failure record on success
    if (response.config) {
      const requestKey = getRequestKey(response.config);
      clearFailure(requestKey);
      pendingRequests.delete(requestKey);
    }
    return response;
  },
  async (error: AxiosError<ApiError>) => {
    // Handle backoff errors
    if ((error as any).isBackoff) {
      const apiError: ApiError = {
        message: error.message,
        statusCode: 429,
        timestamp: new Date().toISOString(),
      };
      return Promise.reject(apiError);
    }
    
    // Clean up pending request
    if (error.config) {
      const requestKey = getRequestKey(error.config);
      pendingRequests.delete(requestKey);
    }
    
    // Handle network errors
    if (!error.response) {
      if (error.config) {
        const requestKey = getRequestKey(error.config);
        recordFailure(requestKey);
      }

      const apiError: ApiError = {
        message: 'Network error. Please check your internet connection.',
        statusCode: 0,
        timestamp: new Date().toISOString(),
      };

      return Promise.reject(apiError);
    }

    const { status, data } = error.response;

    // Handle 401 Unauthorized
    if (status === 401) {
      // Check if this is a login request (no auth token in request)
      const isLoginRequest = error.config?.url?.includes('/auth/login') || 
                             error.config?.url?.includes('/Auth/login');
      
      if (isLoginRequest) {
        // For login requests, don't clear token or redirect - just pass the error through
        // The login form will handle displaying the error
        return Promise.reject(error);
      }
      
      // For authenticated requests, handle as session expired
      handleUnauthorizedError();
      const apiError: ApiError = {
        message: 'Your session has expired. Please log in again.',
        statusCode: 401,
        timestamp: new Date().toISOString(),
      };
      
      // Show error notification
      notifyError(apiError);
      
      return Promise.reject(apiError);
    }

    // Handle 403 Forbidden - Show permission denied message
    if (status === 403) {
      // Check if this is a suspended school error
      const isSuspendedSchool =
        (data as any)?.error === 'SchoolSuspended' ||
        data?.message?.toLowerCase().includes('suspended') ||
        data?.message?.toLowerCase().includes('account has been suspended');

      if (isSuspendedSchool) {
        const userRole = data?.data?.userRole;
        if (userRole) sessionStorage.setItem('ark_status_role', userRole);
        if (window.location.pathname !== '/suspended') {
          window.location.href = '/suspended';
        }
        return Promise.reject({ ...error, response: { ...error.response, data: { message: data?.message || 'Your school account has been suspended', data: (data as any)?.data, statusCode: 403 } } });
      }

      // Check if this is a deactivated school error
      const isDeactivatedSchool =
        (data as any)?.error === 'SchoolDeactivated' ||
        data?.message?.toLowerCase().includes('deactivated');

      if (isDeactivatedSchool) {
        const userRole = data?.data?.userRole;
        if (userRole) sessionStorage.setItem('ark_status_role', userRole);
        if (window.location.pathname !== '/deactivated') {
          window.location.href = '/deactivated';
        }
        return Promise.reject({ ...error, response: { ...error.response, data: { message: data?.message || 'Your school has been deactivated', data: (data as any)?.data, statusCode: 403 } } });
      }

      // Check if this is a trial ended error
      const isTrialEnded =
        data?.message?.toLowerCase().includes('trial has ended');

      if (isTrialEnded) {
        const userRole = data?.data?.userRole;
        if (userRole) sessionStorage.setItem('ark_status_role', userRole);
        if (window.location.pathname !== '/trial-ended') {
          window.location.href = '/trial-ended';
        }
        return Promise.reject({ ...error, response: { ...error.response, data: { message: data?.message || 'Your trial has ended', data: (data as any)?.data, statusCode: 403 } } });
      }
      
      // Regular permission denied
      handleForbiddenError();
      const apiError: ApiError = {
        message: data?.message || 'You do not have permission to perform this action.',
        statusCode: 403,
        timestamp: new Date().toISOString(),
      };
      
      // Don't show automatic notification for 403 errors
      // Individual pages/components can handle permission errors as needed
      // notifyError(apiError);
      
      return Promise.reject(apiError);
    }

    // Handle 400 Bad Request - Extract validation errors
    if (status === 400) {
      // Log the full error response for debugging
      console.error('400 Bad Request - Full response:', data);
      
      const validationErrors = extractValidationErrors(data);
      const apiError: ApiError = {
        message: data?.message || 'Validation error. Please check your input.',
        errors: validationErrors,
        statusCode: 400,
        timestamp: new Date().toISOString(),
      };
      
      // Show error notification (forms will handle field-specific errors)
      notifyError(apiError);
      
      return Promise.reject(apiError);
    }

    // Handle 500 Internal Server Error - Generic error message
    if (status === 500) {
      const apiError: ApiError = {
        message: 'An unexpected error occurred on the server. Please try again later.',
        statusCode: 500,
        timestamp: new Date().toISOString(),
      };
      
      // Show error notification
      notifyError(apiError);
      
      return Promise.reject(apiError);
    }

    // Handle other errors
    const apiError: ApiError = {
      message: data?.message || 'An error occurred. Please try again.',
      statusCode: status,
      timestamp: new Date().toISOString(),
    };
    
    // Check for custom header to skip notification
    if ((error.config?.headers as any)?.['X-Skip-Error-Notification']) {
      return Promise.reject(apiError);
    }

    // Show error notification
    notifyError(apiError);
    
    return Promise.reject(apiError);
  }
);

// Handle 401 Unauthorized errors
const handleUnauthorizedError = () => {
  console.log('401 Unauthorized - Token expired or invalid, logging out...');
  
  // Clear auth token immediately
  localStorage.removeItem('ark_portal_token');
  
  // Call the auth store's logout if available
  if (clearAuthToken) {
    clearAuthToken();
  }
  
  // Redirect to login page immediately
  if (window.location.pathname !== '/login') {
    // Don't store redirect for expired sessions - just go to login
    window.location.href = '/login';
  }
};

// Handle 403 Forbidden errors
const handleForbiddenError = () => {
  // Log permission denied error
  console.error('Permission denied: You do not have access to this resource');
  // Notification is now handled in the interceptor
};

// Extract validation errors from API response
const extractValidationErrors = (data: any): Record<string, string[]> => {
  if (!data) return {};
  
  // ASP.NET Core validation errors format: { errors: { FieldName: ["error1", "error2"] } }
  if (data.errors && typeof data.errors === 'object') {
    // Convert PascalCase field names to camelCase for consistency
    const normalizedErrors: Record<string, string[]> = {};
    Object.entries(data.errors).forEach(([field, messages]) => {
      // Convert first character to lowercase for camelCase
      const camelCaseField = field.charAt(0).toLowerCase() + field.slice(1);
      normalizedErrors[camelCaseField] = Array.isArray(messages) ? messages : [String(messages)];
    });
    return normalizedErrors;
  }
  
  // Try to extract errors from different possible formats
  const errors: Record<string, string[]> = {};
  
  // Check if there's a message that can be used as a general error
  if (data.message || data.title) {
    errors.general = [data.message || data.title];
  }
  
  return errors;
};



export default apiClient;
